import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Minus, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('250ml');
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      if (product.price_125ml) {
        setSelectedSize('125ml');
      } else if (product.price_250ml) {
        setSelectedSize('250ml');
      }
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      navigate('/shop');
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    const price = selectedSize === '125ml' ? product.price_125ml : product.price_250ml;

    addToCart({
      product_id: product.id,
      product_name: product.name,
      size: selectedSize,
      price: price,
      quantity: quantity,
      image_url: product.image_url,
    });

    toast.success(`Added ${product.name} to cart!`);
    
    // Redirect to cart page
    navigate('/cart');
  };

  const getCurrentPrice = () => {
    if (!product) return 0;
    return selectedSize === '125ml' ? product.price_125ml : product.price_250ml;
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Product Header Section */}
        <div className="grid lg:grid-cols-2 gap-16 mb-16">
          {/* Product Image */}
          <div className="flex items-center justify-center bg-purple-25 rounded-2xl p-12">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-3/4 max-w-sm h-auto object-contain"
            />
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-5xl font-heading font-light mb-6">{product.name}</h1>
            
            {product.headline && (
              <h2 className="text-2xl font-heading font-light mb-4 text-stone-700">
                {product.headline}
              </h2>
            )}

            {/* Key Benefits */}
            {product.benefits && product.benefits.length > 0 && (
              <div className="mb-8">
                <ul className="space-y-3">
                  {product.benefits.slice(0, 4).map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-purple-600 mr-3 font-bold text-lg">•</span>
                      <span className="text-stone-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Size Selection */}
            <div className="mb-6">
              <p className="text-sm font-medium uppercase tracking-wider mb-3">SIZE</p>
              <div className="flex gap-3">
                {product.price_125ml && (
                  <Button
                    variant={selectedSize === '125ml' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setSelectedSize('125ml')}
                  >
                    125ML
                  </Button>
                )}
                {product.price_250ml && (
                  <Button
                    variant={selectedSize === '250ml' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setSelectedSize('250ml')}
                  >
                    250ML
                  </Button>
                )}
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex gap-4 mb-6">
              <div className="flex items-center border border-stone-300 rounded">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="px-4 font-medium">{quantity}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <Button
                onClick={handleAddToCart}
                className="flex-1 bg-black hover:bg-stone-800 text-white h-12 text-base font-medium"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                ADD TO CART - ${getCurrentPrice()?.toLocaleString()}
              </Button>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="max-w-6xl mx-auto space-y-16">
          
          {/* RECTRAIL Specific Content */}
          {product.id === 'rectrail' && (
            <>
              {/* Hero Section */}
              <div className="py-12">
                <h2 className="text-5xl font-heading font-light text-center mb-8">
                  Turn back joint aging — starting today.
                </h2>
                
                <div className="max-w-4xl mx-auto space-y-6 text-lg text-stone-700 leading-relaxed">
                  <p>
                    <strong>Osteoarthritis doesn't have to win.</strong> Excess ROS (Reactive Oxygen Species) inflames and 
                    destroys cartilage. <span className="text-purple-700 font-semibold">Recatril</span>'s SAC® technology 
                    neutralizes ROS, stops inflammation, and protects your joints before the damage is done. Move freely 
                    again. Feel the difference in weeks.
                  </p>
                  
                  <p>
                    <strong>Don't wait for the next ache.</strong> Add <span className="text-purple-700 font-semibold">Recatril</span> to 
                    your cart now and reclaim the active life you deserve.
                  </p>
                  
                  <div className="bg-stone-50 p-8 rounded-2xl mt-8">
                    <p className="text-stone-800 italic">
                      "Uncontrolled oxidative stress → excess free radicals → cell damage → degenerative diseases (heart disease, 
                      arthritis, osteoporosis, and implied neurodegeneration). <span className="text-purple-700 font-semibold">Recatril</span> neutralizes 
                      free radicals → protects cells → reduces oxidative stress → helps prevent or slow these conditions."
                    </p>
                  </div>
                </div>
              </div>

              {/* X-Ray Comparison and Recatril vs Glucosamine */}
              <div className="py-16 bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organic-healing-2/artifacts/2utp3alf_image.png" 
                  alt="Arthritis Hand vs Healthy Hand and Recatril vs Glucosamine Comparison" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Minerals Included and Renew Section */}
              <div className="py-16 bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organic-healing-2/artifacts/l51zw6l6_image.png" 
                  alt="Minerals Included - Renew. Restore. Replenish." 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Reviews Section */}
              <div className="py-16 bg-stone-50 rounded-2xl px-8">
                <h2 className="text-4xl font-heading font-light text-center mb-12">Reviews</h2>
                
                <div className="max-w-4xl mx-auto space-y-8">
                  {/* Review 1 - JOHN */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-4">
                      <span className="font-bold text-stone-900">JOHN,</span> I used to stare at the walker by the door, counting the days I had left to live. Then I started Marah-Cel. Six months later I'm chasing sunsets on the rooftop, oxygen tank under my arm, six laps in, smiling like a kid again. I take the stairs just because I can. I tie my shoes, pet the dog, hand my wife roses—her laugh still brand-new. The walker? It's in the garage, gathering dust. Marah-Cel didn't give me calcium. It gave me my life back. My second heartbeat.
                    </p>
                  </div>

                  {/* Review 2 - MARY */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed">
                      <span className="font-bold text-stone-900">MARY</span> Fantastic! Bone Health Bundle Verified by shop Canada <span className="text-yellow-500">★★★★★</span> <span className="text-stone-500">08/30/2025</span>
                    </p>
                  </div>

                  {/* Review 3 - MARY A. */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-2">
                      <span className="font-bold text-stone-900">MARY A.</span> I was having to use a walker and had no energy but now I can carry my oxygen and walk unassisted! I also do 6 laps on the rooftop walk about each evening and a half hr of lazy fit on my phone app as well I now take the stairs going down to the main floor several times a day which is 7 steps and 11 flights as I live on the 5th floor so it is really working! I have also just put in a drinking water tap!
                    </p>
                    <p className="text-stone-500">Canada <span className="text-yellow-500">★★★★★</span> 08/13/2025</p>
                  </div>

                  {/* Review 4 - CASSANDRA */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-2">
                      <span className="font-bold text-stone-900">CASSANDRA,</span> I have been taking the Marah-Cel for about 6 months now. Originally before I even started it, I had several health problems. My hormones were out of whack, thyroid out of whack, high cholesterol, high blood pressure, high liver enzymes and terrible anxiety and panic attacks. When I started the Marah-Cel though, I started because I had an awful fall on January 17th 2025, where I broke my tibia, fibula and an ankle bone. My tibia actually came out of my leg. It was brutal. I had to have emergency surgery. Was told it would take 6 months to a year before I would be able to walk again. Now my leg has healed incredibly well and fast, but my testimony for today has to do with all my other blood markers. For the first time in years, all my blood work is trending for the better. I usually hate having my blood drawn but am looking forward to seeing the results in another 6 months of being on Marah-Cel!
                    </p>
                    <p className="text-stone-500">USA <span className="text-yellow-500">★★★★★</span> 07/14/2025</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* TEEN-CEL Specific Content */}
          {product.id === 'teen-cel' && (
            <>
              {/* Hero Section with Image */}
              <div className="text-center py-12">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organic-healing-2/artifacts/01fv5otk_image.png" 
                  alt="Teen jumping in field" 
                  className="w-full h-auto object-contain rounded-2xl mb-8"
                />
                <h2 className="text-5xl font-heading font-light mb-6">
                  For the Future Leaders & Change-Makers
                </h2>
                <p className="text-xl text-stone-700 max-w-3xl mx-auto leading-relaxed">
                  Navigating the teenage years demands more than just good nutrition—it requires targeted support 
                  for rapid growth, mental clarity, and resilient bones. Teen-Cel provides the essential minerals 
                  and calcium your teen needs to thrive academically, physically, and emotionally.
                </p>
              </div>

              {/* Why Teens Need Teen-Cel */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 py-16 px-8 rounded-2xl">
                <h2 className="text-4xl font-heading font-light text-center mb-8">
                  Why Teens Need Teen-Cel
                </h2>
                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <span className="text-purple-600 text-2xl font-bold">🦴</span>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Peak Bone Mass Development</h3>
                        <p className="text-stone-700">
                          During adolescence, up to 90% of peak bone mass is accumulated. Inadequate calcium 
                          intake now can lead to osteoporosis later in life.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-purple-600 text-2xl font-bold">🧠</span>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Cognitive Function & Focus</h3>
                        <p className="text-stone-700">
                          Essential minerals support neurotransmitter function, helping teens stay focused during 
                          study sessions and maintain mental clarity throughout demanding school days.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <span className="text-purple-600 text-2xl font-bold">⚡</span>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Energy & Athletic Performance</h3>
                        <p className="text-stone-700">
                          Magnesium and calcium work together to support muscle function, reduce fatigue, 
                          and optimize physical performance for active teens.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-purple-600 text-2xl font-bold">😌</span>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Stress & Mood Support</h3>
                        <p className="text-stone-700">
                          GABA and magnesium help regulate stress response and promote calm, supporting 
                          emotional balance during this transformative life stage.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Minerals Included */}
              <div className="py-16">
                <h2 className="text-4xl font-heading font-light text-center mb-12">Minerals Included</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-3xl font-bold text-purple-700">Ca</span>
                    </div>
                    <p className="font-semibold text-stone-800">CALCIUM</p>
                    <p className="text-sm text-stone-600">Bone strength</p>
                  </div>
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-3xl font-bold text-red-700">Se</span>
                    </div>
                    <p className="font-semibold text-stone-800">SELENIUM</p>
                    <p className="text-sm text-stone-600">Antioxidant</p>
                  </div>
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-3xl font-bold text-gray-700">Zn</span>
                    </div>
                    <p className="font-semibold text-stone-800">ZINC</p>
                    <p className="text-sm text-stone-600">Immune support</p>
                  </div>
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-3xl font-bold text-green-700">Mg</span>
                    </div>
                    <p className="font-semibold text-stone-800">MAGNESIUM</p>
                    <p className="text-sm text-stone-600">Energy & calm</p>
                  </div>
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-xl font-bold text-blue-700">GABA</span>
                    </div>
                    <p className="font-semibold text-stone-800">GABA</p>
                    <p className="text-sm text-stone-600">Mood balance</p>
                  </div>
                </div>
              </div>

              {/* Renew. Restore. Replenish. Section */}
              <div className="py-16 bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organic-healing-2/artifacts/sp22gqse_image.png" 
                  alt="Renew. Restore. Replenish." 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Reviews Section */}
              <div className="py-16 bg-stone-50 rounded-2xl px-8">
                <h2 className="text-4xl font-heading font-light text-center mb-12">What Parents Are Saying</h2>
                <div className="max-w-4xl mx-auto space-y-8">
                  <div className="bg-white p-8 rounded-xl shadow-md border border-stone-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-bold text-stone-900">Sarah M.</p>
                        <p className="text-sm text-stone-600">Mother of 15-year-old</p>
                      </div>
                      <div className="text-right">
                        <div className="text-yellow-500 text-lg">★★★★★</div>
                        <p className="text-xs text-stone-500">Verified Purchase</p>
                      </div>
                    </div>
                    <p className="text-stone-700 leading-relaxed">
                      "My son has been taking Teen-Cel for 6 months and the difference is remarkable. His focus has 
                      improved, he's less stressed during exam season, and his growth spurt seems to be going smoothly. 
                      As a mom, I feel confident knowing he's getting the essential nutrients for this critical stage."
                    </p>
                  </div>

                  <div className="bg-white p-8 rounded-xl shadow-md border border-stone-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-bold text-stone-900">Michael T.</p>
                        <p className="text-sm text-stone-600">Father of 16-year-old athlete</p>
                      </div>
                      <div className="text-right">
                        <div className="text-yellow-500 text-lg">★★★★★</div>
                        <p className="text-xs text-stone-500">Verified Purchase</p>
                      </div>
                    </div>
                    <p className="text-stone-700 leading-relaxed">
                      "My daughter plays competitive volleyball and Teen-Cel has been a game-changer. Her recovery 
                      time is faster, she has more energy for practice, and she hasn't had any stress fractures despite 
                      the intense training schedule. Highly recommend for active teens!"
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* NEO-CEL Specific Content */}
          {product.id === 'neo-cel' && (
            <>
              {/* Hero Section - Centered Text Content */}
              <div className="py-12 text-center max-w-3xl mx-auto">
                <p className="text-lg text-stone-700 leading-relaxed mb-6">
                  Chronic stress + poor lifestyle = faster osteoporosis, arthritis, heart issues, anxiety, hormone chaos, weak immunity.
                </p>
                <p className="text-lg text-stone-700 leading-relaxed mb-6">
                  <strong>NEO-CEL flips the switch.</strong> SAC® ionic calcium floods your bloodstream in minutes, igniting your body's own repair crew to:
                </p>
                <ul className="text-lg text-stone-700 space-y-2 mb-6">
                  <li>• Neutralize free radicals</li>
                  <li>• Crush oxidative stress</li>
                  <li>• Rebuild cells & bones</li>
                  <li>• Restore balance and energy</li>
                </ul>
                <p className="text-lg text-stone-700 leading-relaxed mb-4">
                  One daily dose. Real protection. Real vitality.
                </p>
                <p className="text-lg text-stone-800 font-semibold italic">
                  Don't just age—defy it. Add Neo-Cel to cart now.
                </p>
              </div>

              {/* Neo-Cel vs Caltrate Comparison */}
              <div className="py-16 bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/1umcz5u5_image.png" 
                  alt="Neo-Cel vs. Caltrate Comparison" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Minerals Included Section */}
              <div className="py-16 bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/m7z9adxv_image.png" 
                  alt="Minerals Included - Calcium, Selenium, Zinc, Magnesium, GABA" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Reviews Section */}
              <div className="py-16 bg-stone-50 rounded-2xl px-8">
                <h2 className="text-4xl font-heading font-light text-center mb-12">Reviews</h2>
                
                <div className="max-w-4xl mx-auto space-y-8">
                  {/* Review 1 - JOHN */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-4">
                      <span className="font-bold text-stone-900">JOHN,</span> I used to stare at the walker by the door, counting the days I had left to live. Then I started Marah-Cel. Six months later I'm chasing sunsets on the rooftop, oxygen tank under my arm, six laps in, smiling like a kid again. I take the stairs just because I can. I tie my shoes, pet the dog, hand my wife roses—her laugh still brand-new. The walker? It's in the garage, gathering dust. Marah-Cel didn't give me calcium. It gave me my life back. My second heartbeat.
                    </p>
                  </div>

                  {/* Review 2 - MARY */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed">
                      <span className="font-bold text-stone-900">MARY</span> Fantastic! Bone Health Bundle Verified by shop Canada <span className="text-yellow-500">★★★★★</span> <span className="text-stone-500">08/30/2025</span>
                    </p>
                  </div>

                  {/* Review 3 - MARY A. */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-2">
                      <span className="font-bold text-stone-900">MARY A.</span> I was having to use a walker and had no energy but now I can carry my oxygen and walk unassisted! I also do 6 laps on the rooftop walk about each evening and a half hr of lazy fit on my phone app as well I now take the stairs going down to the main floor several times a day which is 7 steps and 11 flights as I live on the 5th floor so it is really working! I have also just put in a drinking water tap!
                    </p>
                    <p className="text-stone-500">Canada <span className="text-yellow-500">★★★★★</span> 08/13/2025</p>
                  </div>

                  {/* Review 4 - CASSANDRA */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-stone-700 leading-relaxed mb-2">
                      <span className="font-bold text-stone-900">CASSANDRA,</span> I have been taking the Marah-Cel for about 6 months now. Originally before I even started it, I had several health problems. My hormones were out of whack, thyroid out of whack, high cholesterol, high blood pressure, high liver enzymes and terrible anxiety and panic attacks. When I started the Marah-Cel though, I started because I had an awful fall on January 17th 2025, where I broke my tibia, fibula and an ankle bone. My tibia actually came out of my leg. It was brutal. I had to have emergency surgery. Was told it would take 6 months to a year before I would be able to walk again. Now my leg has healed incredibly well and fast, but my testimony for today has to do with all my other blood markers. For the first time in years, all my blood work is trending for the better. I usually hate having my blood drawn but am looking forward to seeing the results in another 6 months of being on Marah-Cel!
                    </p>
                    <p className="text-stone-500">USA <span className="text-yellow-500">★★★★★</span> 07/14/2025</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* MARAH-CEL Specific Content */}
          {product.id === 'marah-cel' && (
            <>
              {/* Hero Section - Centered Text Content */}
              <div className="py-8 text-center max-w-3xl mx-auto">
                <p className="text-xl text-stone-700 leading-relaxed mb-6">
                  Oxidative stress drives heart disease, arthritis, and osteoporosis. MARAH-CEL neutralizes free radicals and protects cells using the highest concentration of SAC® (Sigma-Anti Bonding Calcium). In liquid form, SAC® is 200× more soluble and 3× faster acting than traditional calcium pills.
                </p>
                <div className="bg-stone-50 p-6 rounded-xl">
                  <p className="text-xl text-stone-700 italic leading-relaxed">
                    "Uncontrolled oxidative stress → excess free radicals → cell damage → degenerative diseases (heart disease, arthritis, osteoporosis, and implied neurodegeneration). MARAH-CEL neutralizes free radicals → protects cells → reduces oxidative stress → helps prevent or slow these conditions."
                  </p>
                </div>
              </div>

              {/* Section 2 - Benefits and Minerals */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/1r6nbfsi_image.png" 
                  alt="MARAH-CEL Benefits and Minerals Included" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 3 - What Makes Different + Renew Restore */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/bghdbktf_image.png" 
                  alt="What Makes This Natural Wellness Supplement Different" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 4 - Reviews */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/0msi116k_image.png" 
                  alt="Customer Reviews" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </>
          )}

          {/* DENTOSSO Specific Content */}
          {product.id === 'dentosso' && (
            <>
              {/* Section 1 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/3gnja8w6_image.png" 
                  alt="DENTOSSO Content Section 1" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 2 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/3zg7bmum_image.png" 
                  alt="DENTOSSO Content Section 2" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 3 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/bhnzan3t_image.png" 
                  alt="DENTOSSO Content Section 3" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 4 - Reviews */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/5gqmajf8_image.png" 
                  alt="Customer Reviews" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </>
          )}

          {/* KIDS-CEL Specific Content */}
          {product.id === 'kids-cel' && (
            <>
              {/* Hero Section - Centered Text Content */}
              <div className="py-8 text-center max-w-3xl mx-auto">
                <p className="text-xl text-stone-700 leading-relaxed mb-6">
                  Stronger bones. Healthier future. One tiny daily dose. Give your kids the advantage they deserve—add KIDS-CEL to cart now.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                  <p className="text-stone-700">• Smart calcium for growing minds and bodies.</p>
                  <p className="text-stone-700">• Pure support for stronger, brighter kids.</p>
                  <p className="text-stone-700">• Science-powered growth, naturally.</p>
                  <p className="text-stone-700">• Building strong bones for bright tomorrows.</p>
                  <p className="text-stone-700">• Clean, safe, and made for growing up strong.</p>
                  <p className="text-stone-700">• The natural way to nurture healthy growth.</p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/zjn2srah_image.png" 
                  alt="KIDS-CEL Content Section 2" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 3 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/6gth8iw1_image.png" 
                  alt="KIDS-CEL Content Section 3" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 4 - Reviews */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/u9p297yh_image.png" 
                  alt="Customer Reviews" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </>
          )}

          {/* ALZI-CEL Specific Content */}
          {product.id === 'alzi-cel' && (
            <>
              {/* Hero Section - Centered Text Content */}
              <div className="py-8 text-center max-w-3xl mx-auto">
                <p className="text-xl text-stone-700 leading-relaxed mb-6">
                  ALZI-CEL with SAC® Formulation Technology supports brain wellness and memory preservation by restoring calcium balance in cerebral blood vessels, regulating chloride and potassium channels, and optimizing GABA receptor function.
                </p>
                <p className="text-xl text-stone-700 leading-relaxed mb-6">
                  This helps prevent calcium imbalance linked to neurodegenerative conditions, promotes overall health, and provides added free-radical protection.
                </p>
                <p className="text-xl text-stone-700 leading-relaxed">
                  ALZI-CEL helps restore natural calcium balance in the brain, supporting healthy nerve function and calm communication between neurons. By maintaining this balance, it contributes to mental clarity, relaxation, and overall brain wellness.
                </p>
              </div>

              {/* Section 2 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/mkuuminf_image.png" 
                  alt="ALZI-CEL Content Section 2" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 3 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/h7fktjno_image.png" 
                  alt="ALZI-CEL Content Section 3" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 4 */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/cdbi05fb_image.png" 
                  alt="ALZI-CEL Content Section 4" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>

              {/* Section 5 - Reviews */}
              <div className="bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/601cs8fb_image.png" 
                  alt="Customer Reviews" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </>
          )}
          
          {/* SAC-SG Specific Content */}
          {product.id === 'sac-sg' && (
          <>
          {/* Hero Tagline Section */}
          <div className="text-center py-8">
            <h2 className="text-5xl font-heading font-light mb-8">
              The One Formula Cancer Cells Can't Beat
            </h2>
            <div className="max-w-4xl mx-auto space-y-4 text-lg text-stone-700">
              <div className="flex items-start gap-4">
                <span className="text-purple-600 text-2xl font-bold">•</span>
                <p><strong>Healthy cells:</strong> shielded, energized, unstoppable</p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-purple-600 text-2xl font-bold">•</span>
                <p>
                  <strong>Irregular cells:</strong> hit with 200-300x faster ionic calcium + glucose overload - 
                  proven to trigger precise self-destruction while normal cells thrive (real cancer metabolism research)
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-purple-600 text-2xl font-bold">•</span>
                <p><strong>Bones & teeth:</strong> visibly stronger, faster</p>
              </div>
            </div>
          </div>

          {/* Renew. Restore. Replenish. Section */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 py-16 px-8 rounded-2xl">
            <h2 className="text-4xl font-heading font-light text-center mb-6">
              Renew. Restore. Replenish.
            </h2>
            <p className="text-center text-lg text-stone-700 max-w-3xl mx-auto">
              SAC® delivers free, active calcium for instant absorption—no digestion needed. 
              Unlike bound calcium, it triggers natural healing, balances physiology, boosts immunity, 
              neutralizes free radicals, and detoxifies the body for renewed wellness and vitality.
            </p>
          </div>

          {/* Oxidative Stress Section */}
          <div className="py-12">
            <div className="max-w-4xl mx-auto">
              <p className="text-center text-lg text-stone-700 leading-relaxed">
                Uncontrolled oxidative stress → excess free radicals → cell damage → degenerative diseases 
                (heart disease, arthritis, osteoporosis, and implied neurodegeneration). 
                <span className="font-semibold text-purple-700"> SAC-SG neutralizes free radicals → protects cells → 
                reduces oxidative stress → helps prevent or slow these conditions.</span>
              </p>
            </div>
          </div>

          {/* Normal Cell vs Cancer Cell Section */}
          <div className="text-center py-16 bg-stone-50 rounded-2xl">
            <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto px-8">
              <div>
                <h3 className="text-3xl font-heading font-light mb-6">NORMAL CELL</h3>
                <div className="flex justify-center mb-4">
                  <div className="w-48 h-48 rounded-full bg-gradient-to-br from-pink-200 to-orange-200 flex items-center justify-center shadow-lg">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-300 to-orange-300"></div>
                  </div>
                </div>
                <p className="text-stone-600">Smooth, organized, and healthy structure</p>
              </div>
              <div>
                <h3 className="text-3xl font-heading font-light mb-6">CANCER CELL</h3>
                <div className="flex justify-center mb-4">
                  <div className="relative w-48 h-48">
                    <div className="absolute top-0 left-0 w-32 h-32 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 blur-sm shadow-md"></div>
                    <div className="absolute top-8 right-0 w-28 h-28 rounded-full bg-gradient-to-br from-pink-200 to-orange-200 blur-sm shadow-md"></div>
                    <div className="absolute bottom-0 left-8 w-24 h-24 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 blur-sm shadow-md"></div>
                  </div>
                </div>
                <p className="text-stone-600">Chaotic, irregular, and proliferated</p>
              </div>
            </div>
          </div>

          {/* Calcium-Glucose Overload Mechanism Section */}
          <div className="bg-white py-16 px-8 rounded-2xl border border-stone-200">
            <h2 className="text-4xl font-heading font-light text-center mb-4">
              The Calcium-Glucose Overload Mechanism in Cancer Cells
            </h2>
            <p className="text-center text-stone-600 mb-12">(SAC-SG Studies – and what science says)</p>

            <div className="max-w-4xl mx-auto mb-12">
              <p className="text-lg mb-8 text-stone-700">
                Cancer cells behave very differently from normal cells in two key ways that create a deadly "Achilles' heel":
              </p>

              {/* Comparison Table */}
              <div className="overflow-x-auto mb-12">
                <table className="w-full border-collapse shadow-md">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="border border-stone-300 px-6 py-4 text-left font-semibold">Trait</th>
                      <th className="border border-stone-300 px-6 py-4 text-left font-semibold">Normal Cells</th>
                      <th className="border border-stone-300 px-6 py-4 text-left font-semibold">Cancer Cells (Warburg effect)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    <tr>
                      <td className="border border-stone-300 px-6 py-4 font-medium">Glucose uptake</td>
                      <td className="border border-stone-300 px-6 py-4">Moderate, tightly regulated</td>
                      <td className="border border-stone-300 px-6 py-4">10–100x higher – they're glucose addicts</td>
                    </tr>
                    <tr className="bg-stone-50">
                      <td className="border border-stone-300 px-6 py-4 font-medium">Calcium regulation</td>
                      <td className="border border-stone-300 px-6 py-4">Very strict – keep cytosolic Ca²⁺ low</td>
                      <td className="border border-stone-300 px-6 py-4">Already fragile – easily overwhelmed by extra Ca²⁺</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mechanism Steps */}
              <h3 className="text-2xl font-heading font-light mb-8 text-center">The overload mechanism step-by-step</h3>
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">1</div>
                  <p className="text-stone-700 pt-1">
                    <strong>SAC-SG delivers free ionic Ca²⁺ (Calcium ion) + glucose</strong> → Absorbed 200–300x faster 
                    than normal calcium forms (based on algae-derived ionic form).
                  </p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">2</div>
                  <p className="text-stone-700 pt-1">
                    <strong>Cancer cells gobble both</strong> because they are starving for glucose and have upregulated 
                    calcium channels, they take in the SAC-SG combo at dramatically higher rates than healthy cells.
                  </p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">3</div>
                  <div className="pt-1">
                    <p className="text-stone-700 mb-4">
                      <strong>Massive cytosolic Ca²⁺ spike inside cancer cells.</strong> This triggers the "calcium overload" cascade:
                    </p>
                    <ul className="space-y-3 ml-6">
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold">→</span>
                        <span className="text-stone-700">Mitochondria suck in huge amounts of Ca²⁺ → mitochondrial membrane collapse</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold">→</span>
                        <span className="text-stone-700">Massive ROS (reactive oxygen species) explosion</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold">→</span>
                        <span className="text-stone-700">Endoplasmic reticulum stress → unfolded protein response</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold">→</span>
                        <span className="text-stone-700">Activation of calpain and other proteases → Apoptosis or necrosis within hours</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-lg">4</div>
                  <p className="text-stone-700 pt-1">
                    <strong>Healthy cells stay safe.</strong> They take up far less of the mixture, and their stronger 
                    calcium pumps (PMCA, SERCA) quickly export excess Ca²⁺, so they experience only mild beneficial signaling.
                  </p>
                </div>
              </div>
            </div>

            {/* Scientific References */}
            <div className="mt-12 pt-8 border-t border-stone-200">
              <h4 className="text-xl font-heading font-light mb-4 text-center">Real published science that supports the concept</h4>
              <p className="text-center text-purple-700 font-medium">
                Danese et al, 2022 (Cancers); Zhivotovsky & Orrenius, 2011–2024, SAC® Studies 2018–2023
              </p>
            </div>
          </div>

          {/* Minerals Included Section */}
          <div className="py-16">
            <h2 className="text-4xl font-heading font-light text-center mb-12">Minerals Included</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
              {/* Calcium */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-3xl font-bold text-purple-700">Ca</span>
                </div>
                <p className="font-semibold text-stone-800">CALCIUM</p>
                <p className="text-sm text-stone-600">Free ionic form</p>
              </div>
              
              {/* Selenium */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-3xl font-bold text-red-700">Se</span>
                </div>
                <p className="font-semibold text-stone-800">SELENIUM</p>
                <p className="text-sm text-stone-600">Antioxidant support</p>
              </div>
              
              {/* Zinc */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-3xl font-bold text-gray-700">Zn</span>
                </div>
                <p className="font-semibold text-stone-800">ZINC</p>
                <p className="text-sm text-stone-600">Immune function</p>
              </div>
              
              {/* Magnesium */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-3xl font-bold text-green-700">Mg</span>
                </div>
                <p className="font-semibold text-stone-800">MAGNESIUM</p>
                <p className="text-sm text-stone-600">Cellular energy</p>
              </div>
              
              {/* GABA */}
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center shadow-md">
                  <span className="text-xl font-bold text-blue-700">GABA</span>
                </div>
                <p className="font-semibold text-stone-800">GAMMA-AMINOBUTYRIC ACID</p>
                <p className="text-sm text-stone-600">Nervous system</p>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="py-16 bg-stone-50 rounded-2xl px-8">
            <h2 className="text-4xl font-heading font-light text-center mb-12">Reviews</h2>
            <div className="max-w-4xl mx-auto space-y-8">
              
              {/* Intro Testimonial */}
              <div className="bg-white p-8 rounded-xl shadow-md border border-stone-200">
                <p className="text-stone-700 leading-relaxed italic">
                  "I used to stare at the walker by the door, counting the days I had left to live. 
                  Then I started Marah-Cel. Six months later I'm chasing sunsets on the rooftop, 
                  oxygen tank under my arm, six laps in, smiling like a kid again. I take the stairs 
                  just because I can. I tie my shoes, pet the dog, hand my wife roses—her laugh still brand-new. 
                  The walker? It's in the garage, gathering dust. Marah-Cel didn't give me calcium. 
                  It gave me my life back. My second heartbeat."
                </p>
                <p className="mt-4 font-semibold text-stone-800">— JOHN</p>
              </div>

              {/* Review 1 - MARY */}
              <div className="bg-white p-6 rounded-xl shadow-md border border-stone-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-stone-900">MARY</p>
                    <p className="text-sm text-stone-600">Verified by shop Canada</p>
                  </div>
                  <div className="text-right">
                    <div className="text-yellow-500 text-lg">★★★★★</div>
                    <p className="text-xs text-stone-500">08/30/2025</p>
                  </div>
                </div>
                <p className="font-semibold text-purple-700 mb-2">Fantastic! Bone Health Bundle</p>
              </div>

              {/* Review 2 - MARY A. */}
              <div className="bg-white p-6 rounded-xl shadow-md border border-stone-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-stone-900">MARY A.</p>
                    <p className="text-sm text-stone-600">Canada</p>
                  </div>
                  <div className="text-right">
                    <div className="text-yellow-500 text-lg">★★★★★</div>
                    <p className="text-xs text-stone-500">08/13/2025</p>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed">
                  I was having to use a walker and had no energy but now I can carry my oxygen and walk unassisted! 
                  I also do 6 laps on the rooftop walk about each evening and a half hr of lazy fit on my phone app 
                  as well. I now take the stairs going down to the main floor several times a day which is 7 steps 
                  and 11 flights as I live on the 5th floor so it is really working! I have also just put in a 
                  drinking water tap!
                </p>
              </div>

              {/* Review 3 - CASSANDRA */}
              <div className="bg-white p-6 rounded-xl shadow-md border border-stone-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-stone-900">CASSANDRA</p>
                    <p className="text-sm text-stone-600">USA</p>
                  </div>
                  <div className="text-right">
                    <div className="text-yellow-500 text-lg">★★★★★</div>
                    <p className="text-xs text-stone-500">07/14/2025</p>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed">
                  I have been taking the Marah-Cel for about 6 months now. Originally before I even started it, 
                  I had several health problems. My hormones were out of whack, thyroid out of whack, high cholesterol, 
                  high blood pressure, high liver enzymes and terrible anxiety and panic attacks. When I started the 
                  Marah-Cel though, I started because I had an awful fall on January 17th 2025, where I broke my tibia, 
                  fibula and an ankle bone. My tibia actually came out of my leg. It was brutal. I had to have emergency 
                  surgery. Was told it would take 6 months to a year before I would be able to walk again. Now my leg 
                  has healed incredibly well and fast, but my testimony for today has to do with all my other blood 
                  markers. For the first time in years, all my blood work is trending for the better. I usually hate 
                  having my blood drawn, but I am actually looking forward to seeing the results in another 6 months 
                  of being on Marah-Cel!
                </p>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};
